import React, { ComponentProps, ReactNode } from 'react';

import PropTypes from 'prop-types';

import { BasicButton } from '@adsk/alloy-react-button';
import { variantSwitch, stylePropType } from '@adsk/alloy-react-helpers';
import theme from '@adsk/alloy-react-theme';
import Tooltip from '@adsk/alloy-react-tooltip';

import { DATE_PICKER_CELL_VARIANTS } from '../consts';
import { CellVariant } from '../types';

type CellProps = ComponentProps<typeof BasicButton> & {
  variant?: CellVariant;
  selected?: boolean;
  today?: boolean;
  hasEvent?: boolean;
  tooltipContent?: ReactNode;
};

const Cell = React.forwardRef<HTMLButtonElement, CellProps>(
  (
    {
      className,
      style,
      variant = DATE_PICKER_CELL_VARIANTS.DEFAULT,
      selected,
      today,
      disabled,
      hasEvent,
      tooltipContent,
      ...props
    },
    ref
  ) => {
    const children = (
      <BasicButton
        {...props}
        ref={ref}
        className={className}
        tabIndex={variant === DATE_PICKER_CELL_VARIANTS.SPACER ? -1 : undefined}
        style={[
          {
            width: 40,
            height: 40,
            cursor: disabled ? 'default' : 'pointer',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'inline-flex',
            position: 'relative',
          },
          theme.typography.bodyMediumBold,
          variantSwitch({
            statement: variant,
            variants: {
              [DATE_PICKER_CELL_VARIANTS.DEFAULT]: {
                borderRadius: 4,
                color: today
                  ? theme.colors.adskBlue500
                  : theme.colors.charcoal900,

                '&:hover': {
                  backgroundColor: theme.colors.charcoal050,
                },

                '&:focus': {
                  backgroundColor: theme.colors.white,
                  boxShadow: `inset 0px 0px 0px 1px ${theme.colors.adskBlue500}, inset ${theme.halos.focus}`,
                },

                '&:active': {
                  backgroundColor: theme.colors.charcoal100,
                  boxShadow: today
                    ? `inset 0px 0px 0px 1px ${theme.colors.adskBlue500}`
                    : 'none',
                },

                ...(disabled && {
                  color: theme.colors.charcoal500,
                  backgroundColor: theme.colors.white,

                  '&:hover': {
                    boxShadow: 'none',
                    backgroundColor: theme.colors.charcoal050,
                  },

                  '&:focus, &:active': {
                    backgroundColor: 'none',
                    boxShadow: 'none',
                  },
                }),

                ...(selected && {
                  borderRadius: 6,
                  color: theme.colors.white,
                  backgroundColor: theme.colors.adskBlue500,
                  '&:hover, &:focus, &:active': {
                    boxShadow: 'none',
                    backgroundColor: theme.colors.adskBlue500,
                  },
                }),
              },
              [DATE_PICKER_CELL_VARIANTS.RANGE_MIDDLE]: {
                borderRadius: 0,
                backgroundColor: theme.colors.adskBlue500Alpha15,
                '&:hover': {
                  color: theme.colors.white,
                  backgroundColor: theme.colors.adskBlue300,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.RANGE_START]: {
                color: theme.colors.white,
                borderRadius: '4px 0 0 4px',
                backgroundColor: theme.colors.adskBlue500,
                '&:hover': {
                  color: theme.colors.white,
                  backgroundColor: theme.colors.adskBlue700,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.RANGE_END]: {
                color: theme.colors.white,
                borderRadius: '0 4px  4px 0',
                backgroundColor: theme.colors.adskBlue500,
                '&:hover': {
                  color: theme.colors.white,
                  backgroundColor: theme.colors.adskBlue700,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.OUTSIDE]: {
                borderRadius: 4,
                color: theme.colors.charcoal500,
                backgroundColor: theme.colors.white,

                '&:hover, &:focus, &:active': {
                  backgroundColor: theme.colors.charcoal050,
                },
              },
              [DATE_PICKER_CELL_VARIANTS.SPACER]: {
                cursor: 'default',
                pointerEvents: 'none',
                ...theme.typography.bodyMedium,
              },
            },
          }),
          style,
        ]}
      >
        {props.children}
        {hasEvent && (
          <div
            style={{
              position: 'absolute',
              top: 30,
              borderRadius: '50%',
              backgroundColor: selected
                ? theme.colors.white
                : theme.colors.charcoal900,
              minHeight: 6,
              minWidth: 6,
              height: 6,
              width: 6,
              maxHeight: 6,
              maxWidth: 6,
            }}
          />
        )}
      </BasicButton>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          variant={Tooltip.VARIANTS.DARK}
          placement={Tooltip.PLACEMENTS.TOP}
          content={tooltipContent}
          delayShow={0}
          delayHide={0}
          getTargetProps={() => ({
            style: {
              width: 40,
              height: 40,
            },
          })}
        >
          {children}
        </Tooltip>
      );
    }

    return children;
  }
);

Cell.displayName = 'Cell';

Cell.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Day value */
  children: PropTypes.node,
  /**  The style variant */
  variant: PropTypes.oneOf(Object.values(DATE_PICKER_CELL_VARIANTS)),
  /** Whether this day is selected, does not apply when variant is a range variant */
  selected: PropTypes.bool,
  /** Whether this day is the today day, does not apply when variant is a range variant */
  today: PropTypes.bool,
  /** Whether this day is the restricted */
  disabled: PropTypes.bool,
  /** Tooltip content */
  tooltipContent: PropTypes.node,
};

export default Object.assign(Cell, { VARIANTS: DATE_PICKER_CELL_VARIANTS });
